import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  faLifeRing,
  faEnvelope,
  faListCheck,
  faNoteSticky,
  faBullhorn,
  faHeadset,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { LANGUAGE, getCurrencyIconUrl, translate } from "services/general";
import { user, HAS_PERMISSION } from "services/user";
import { WALLET_INDEX } from "permissions";
import { useProfile } from "api/shared";
import { useTopBarCounters } from "modules/topbar/api";
import { useAmerSupportNeedsActionCount } from "modules/amer-support/api";
import { useAnnouncementsUnreadCount } from "modules/announcements/api";

export default function useMoreToolsDropdown({
  isDark,
  moreMenuOpen,
  setMoreMenuOpen,
}) {
  const { t } = useTranslation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { data: profile } = useProfile();
  const { data: counters } = useTopBarCounters();
  const { data: amerSupport } = useAmerSupportNeedsActionCount({
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const { data: announcements } = useAnnouncementsUnreadCount({
    refetchInterval: 300000,
    staleTime: 120000,
  });

  // Lock body scroll ONLY on phone screens (< 640px) when full-screen mobile menu is open
  useEffect(() => {
    if (moreMenuOpen && !isSearchOpen && window.innerWidth < 640) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [moreMenuOpen, isSearchOpen]);

  // Handle escape key
  useEffect(() => {
    if (moreMenuOpen) {
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          setMoreMenuOpen(false);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [moreMenuOpen, setMoreMenuOpen]);

  const closeMenu = () => {
    setMoreMenuOpen(false);
    setIsSearchOpen(false);
  };

  // The result is a link and navigates by itself; this only closes the menu.
  const handleSearchResultClick = () => {
    closeMenu();
  };

  const fullName = `${translate(user?.fname, user?.en_fname) || ""} ${
    translate(user?.lname, user?.en_lname) || ""
  }`.trim();
  const role = user?.roles?.[0]?.name;
  const initial = fullName ? fullName.charAt(0) : "?";

  const currency = user?.agent?.currency;
  const currencyIconUrl = getCurrencyIconUrl(isDark);
  const hasWallet = HAS_PERMISSION(WALLET_INDEX);

  // Each tile has its own fixed hue (blue is `info`, not `primary`), so no palette makes two tiles match.
  const quickNavItems = [
    {
      id: "search",
      icon: faMagnifyingGlass,
      title: t("search") || "البحث الشامل",
      isCustomAction: true,
      onClick: () => setIsSearchOpen(true),
      color: "text-info",
      bg: "bg-info/5 dark:bg-info/10",
    },
    {
      id: "support",
      icon: faLifeRing,
      title: t("messages.title"),
      to: `/${LANGUAGE}/support-center`,
      count: counters?.support_tickets || 0,
      color: "text-info",
      bg: "bg-info/5 dark:bg-info/10",
    },
    {
      id: "contact",
      icon: faEnvelope,
      title: t("pages.contact_messages"),
      to: `/${LANGUAGE}/contact-messages`,
      count: counters?.contact_messages || 0,
      color: "text-[#10B981]",
      bg: "bg-[#ECFDF5] dark:bg-[#10B981]/10",
    },
    {
      id: "tasks",
      icon: faListCheck,
      title: t("pages.tasks"),
      to: `/${LANGUAGE}/tasks`,
      count: counters?.pending_tasks || 0,
      color: "text-[#8B5CF6]",
      bg: "bg-[#F5F3FF] dark:bg-[#8B5CF6]/10",
    },
    {
      id: "notes",
      icon: faNoteSticky,
      title: t("pages.notes"),
      to: `/${LANGUAGE}/notes`,
      color: "text-[#F59E0B]",
      bg: "bg-[#FFFBEB] dark:bg-[#F59E0B]/10",
    },
    {
      id: "announcements",
      icon: faBullhorn,
      title: t("announcements.title"),
      to: `/${LANGUAGE}/announcements`,
      count: announcements?.count || 0,
      color: "text-[#EC4899]",
      bg: "bg-[#FDF2F8] dark:bg-[#EC4899]/10",
    },
    {
      id: "amer-help",
      icon: faHeadset,
      title: t("amerSupport.title") || "مركز مساعدة عامر",
      to: `/${LANGUAGE}/amer-support`,
      count: amerSupport?.count || 0,
      color: "text-[#06B6D4]",
      bg: "bg-[#ECFEFF] dark:bg-[#06B6D4]/10",
    },
  ];

  return {
    t,
    user,
    isSearchOpen,
    setIsSearchOpen,
    profile,
    closeMenu,
    handleSearchResultClick,
    fullName,
    role,
    initial,
    currency,
    currencyIconUrl,
    hasWallet,
    quickNavItems,
  };
}
